import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { X } from 'lucide-react';
import { deleteMDTReport, getMDTReportsById, updateMDTReportStatus } from '../services/mdtReportService';
import { toast } from 'react-toastify';
import { MDTReportStatus, Patient } from '../types';
import { getPatientById } from '../services/patientService';
import Modal from '../components/Common/Modal';
import { extractMDTReportContext } from '../utils/reportUtils';
import { MDTReportRenderer } from '../components/MDTReportRenderer';

const ReportView: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  const from = location.state?.from;
  const patientId = location.state?.patientId;

  const [statusMessage, setStatusMessage] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);
  const [report, setReport] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [patient, setPatient] = useState<Patient | null>(null);

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [mdtToDelete, setMdtToDelete] = useState<number | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const isDoctor = user?.role === 'DOCTOR';
  const reportStatus = report?.status;

  useEffect(() => {
    if (!id) return;
    const fetchData = async () => {
      try {
        setLoading(true);
        const reportData = await getMDTReportsById(id);
        setReport(reportData);
        if (reportData?.patient_id) {
          const patientData = await getPatientById(reportData.patient_id);
          setPatient(patientData);
        }
      } catch (err) {
        console.error(err);
        toast.error('Failed to load data');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id, location.key]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mr-3" />
        <span className="text-gray-600 text-sm">Loading MDT report...</span>
      </div>
    );
  }

  if (!report) {
    return (
      <div className="card p-6">
        <p className="text-gray-600">Report not found.</p>
      </div>
    );
  }

  const parsedContext = extractMDTReportContext(report?.context);
  const agentReasoning = parsedContext.agentReasoning;

  const patientName = patient
    ? `${patient.forename || ''} ${patient.surname || ''}`.trim()
    : report.patient
      ? `${report.patient.forename || ''} ${report.patient.surname || ''}`.trim()
      : 'Michael Anderson';

  const patientAge = patient?.date_of_birth
    ? (() => {
        const dob = new Date(patient.date_of_birth);
        const today = new Date();
        let age = today.getFullYear() - dob.getFullYear();
        if (today.getMonth() < dob.getMonth() || (today.getMonth() === dob.getMonth() && today.getDate() < dob.getDate())) age--;
        return age;
      })()
    : 72;

  const patientGender = patient?.gender || 'Male';

  const handleStatusChange = async (newStatus: MDTReportStatus, successMessage: string) => {
    try {
      setIsUpdating(true);
      const result = await updateMDTReportStatus(report.id, newStatus);
      setReport(result.data);
      setStatusMessage(successMessage);
      toast.success(successMessage);
    } catch (error: any) {
      toast.error(error?.message || error?.response?.data?.message || 'Failed to update report status');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDeleteClick = () => {
    setMdtToDelete(report.id);
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!mdtToDelete) return;
    try {
      setIsDeleting(true);
      await deleteMDTReport(mdtToDelete);
      toast.success('MDT report deleted successfully');
      setIsDeleteModalOpen(false);
      setMdtToDelete(null);
      if (from === 'patient') navigate(`/patients/${patientId}?tab=mdt-reports`);
      else navigate('/reports');
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to delete MDT report');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleBack = () => {
    if (patientId || report.patient_id) navigate(`/patients/${patientId || report.patient_id}?tab=mdt-reports`);
    else navigate('/reports');
  };

  return (
    <div className="min-h-screen bg-[#f5f7fa] py-6">
      {statusMessage && (
        <div className="max-w-[1280px] mx-auto px-8 mb-4">
          <div className="p-3 rounded bg-green-50 text-green-800 border border-green-200 text-sm">{statusMessage}</div>
        </div>
      )}

      <MDTReportRenderer
        reportText={parsedContext.reportText}
        agentReasoning={agentReasoning}
        patientName={patientName}
        patientAge={patientAge}
        patientGender={patientGender}
        parsedContext={parsedContext}
        reportId={report.id}
        reportStatus={reportStatus}
        createdAt={report.createdAt}
        hospital={report.hospital}
        reviewedBy={report.reviewed_by}
        reviewedAt={report.reviewed_at}
        isDoctor={isDoctor}
        isUpdating={isUpdating}
        onStatusChange={handleStatusChange}
        onDelete={handleDeleteClick}
        onBack={handleBack}
      />

      {/* ── FOOTER MEDICAL DISCLAIMER ────────────────────────────────────────── */}
      <footer className="max-w-[1280px] mx-auto px-8 mt-8 pt-4 border-t border-gray-200 flex flex-col sm:flex-row items-center justify-between text-xs text-gray-500 gap-2">
        <span>
          AI-Assisted Clinical Decision Support — Multidisciplinary consultation document. Requires clinician review.
        </span>
        <Link
          to="/legal?tab=medical-disclaimer"
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary-600 hover:text-primary-700 underline font-medium whitespace-nowrap"
        >
          Medical Disclaimer &rarr;
        </Link>
      </footer>

      {/* ── DELETE MODAL ───────────────────────────────────────────────────── */}
      <Modal
        title="Delete MDT Report"
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        widthClassName="max-w-md"
      >
        <div className="space-y-4">
          <p className="text-gray-700">
            Are you sure you want to delete this MDT report?{' '}
            <span className="text-red-600 font-medium">This action cannot be undone.</span>
          </p>
          <div className="flex justify-end space-x-3 pt-4 border-t">
            <button onClick={() => setIsDeleteModalOpen(false)} className="btn-secondary" disabled={isDeleting}>Cancel</button>
            <button onClick={confirmDelete} disabled={isDeleting} className="btn-danger">
              {isDeleting ? 'Deleting...' : 'Delete'}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default ReportView;
